import { SelectedSKU } from '@forms/SKUForm/types';
import {
  DimensionsDraft,
  PalletPanelDraft,
  ProductPanelDraft,
  SkuPanelDraft,
  withoutBlankAdditionalFields,
} from './panelEdit';

const numberOrUndefined = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const textOrNull = (value: string): string | null =>
  value.trim() === '' ? null : value.trim();

const dimensionsPayload = (draft: DimensionsDraft) => ({
  length: numberOrUndefined(draft.length),
  width: numberOrUndefined(draft.width),
  height: numberOrUndefined(draft.height),
  dimensionsUnitOfMeasure: draft.unitOfMeasure,
});

const existingDimensionsPayload = (
  dimensions:
    | {
        length?: number | null;
        width?: number | null;
        height?: number | null;
        dimensionsUnitOfMeasure?: string | null;
      }
    | null
    | undefined
) => ({
  length: dimensions?.length ?? undefined,
  width: dimensions?.width ?? undefined,
  height: dimensions?.height ?? undefined,
  dimensionsUnitOfMeasure: dimensions?.dimensionsUnitOfMeasure,
});

const existingPalletisingPayload = (sku: SelectedSKU['sku']) =>
  (sku.palletising ?? []).map((pallet) => ({
    id: pallet.id,
    type: pallet.type,
    customPalletSpecification: pallet.customPalletSpecification,
    customPalletWeight: pallet.customPalletWeight,
    customPalletWeightUnit: pallet.customPalletWeightUnit,
    amountOfLayersInPallet: pallet.amountOfLayersInPallet,
    amountOfSkusInLayer: pallet.amountOfSkusInLayer,
    dimensions: existingDimensionsPayload(pallet.dimensions),
  }));

const palletDraftPayload = (draft: PalletPanelDraft, id: string) => ({
  id,
  type: draft.type,
  customPalletSpecification: textOrNull(draft.customPalletSpecification),
  customPalletWeight: numberOrUndefined(draft.weight),
  customPalletWeightUnit: draft.weightUnitOfMeasure,
  amountOfLayersInPallet: numberOrUndefined(draft.layers),
  amountOfSkusInLayer: numberOrUndefined(draft.skusPerLayer),
  dimensions: dimensionsPayload(draft.dimensions),
});

const existingMetaDataPayload = (sku: SelectedSKU['sku']) =>
  (sku.metaData ?? []).map((field) => ({
    name: field.name,
    value: field.value,
    type: field.type,
  }));

const existingProductDetailsPayload = (sku: SelectedSKU['sku']) => ({
  productId: sku.productDetails?.productId,
  name: sku.productDetails?.name,
  description: sku.productDetails?.description,
  brand: sku.productDetails?.brand,
  category: sku.productDetails?.category,
  subCategory: sku.productDetails?.subCategory,
  netVolume: sku.productDetails?.netVolume,
  netVolumeUnitOfMeasure: sku.productDetails?.netVolumeUnitOfMeasure,
  barcode: sku.productDetails?.barcode?.barcode,
  barcodeImage: sku.productDetails?.barcodeImage ?? undefined,
  image: sku.productDetails?.image ?? undefined,
  dimensions: sku.productDetails?.dimensions
    ? existingDimensionsPayload(sku.productDetails.dimensions)
    : undefined,
  weight: sku.productDetails?.weight ?? undefined,
  weightUnitOfMeasure: sku.productDetails?.weightUnitOfMeasure ?? undefined,
  supplier: sku.productDetails?.supplier ?? undefined,
});

const baseSkuPayload = (sku: SelectedSKU['sku']) => ({
  inventorySkuId: sku.inventorySkuId,
  createdAt: sku.createdAt,
  skuId: sku.skuId,
  name: sku.name,
  description: sku.description,
  unitOfMeasure: sku.unitOfMeasure,
  amountOfProductInSku: sku.amountOfProductInSku,
  lowStockThreshold: sku.lowStockThreshold ?? undefined,
  barcode: sku.barcode?.barcode,
  barcodeImage: sku.barcodeImage ?? undefined,
  dimensions: existingDimensionsPayload(sku.dimensions),
  grossWeight: sku.grossWeight,
  grossWeightUnitOfMeasure: sku.grossWeightUnitOfMeasure,
  grossVolume: sku.grossVolume ?? undefined,
  grossVolumeUnitOfMeasure: sku.grossVolumeUnitOfMeasure ?? undefined,
  minimumOrderQuantity: sku.minimumOrderQuantity ?? undefined,
  coPackingSupplier: sku.coPackingSupplier ?? undefined,
  costPrices: sku.costPrices ?? [],
  listPrices: sku.listPrices ?? [],
  palletising: existingPalletisingPayload(sku),
  metaData: existingMetaDataPayload(sku),
  productDetails: existingProductDetailsPayload(sku),
  temperatureControlRequirements: sku.temperatureControlRequirements,
  suppliers: sku.suppliers ?? [],
});

type ImageUpload = { id: string; title: string; image: File };

const imageUploadPayload = (imageFile: File): ImageUpload => ({
  id: crypto.randomUUID(),
  title: imageFile.name,
  image: imageFile,
});

type SkuImageChange = {
  skuImages: ImageUpload[];
  deletedSkuImageIds: string[];
};

const noSkuImageChange = (): SkuImageChange => ({
  skuImages: [],
  deletedSkuImageIds: [],
});

const skuImageChangePayload = (
  sku: SelectedSKU['sku'],
  chosenImageFile: File | null
): SkuImageChange => {
  if (!chosenImageFile) return noSkuImageChange();
  const replacedSkuImageId = sku.images?.[0]?.id;
  return {
    skuImages: [imageUploadPayload(chosenImageFile)],
    deletedSkuImageIds: replacedSkuImageId ? [replacedSkuImageId] : [],
  };
};

const asUpdateRequest = (
  skuPayload: object,
  skuImageChange = noSkuImageChange()
) => ({
  sku: skuPayload,
  skuImages: skuImageChange.skuImages,
  barcodeImage: {},
  deletedSkuImageIds: skuImageChange.deletedSkuImageIds,
  deletedSkuBarcodeImageIds: [],
  productBarcodeImage: {},
  deletedProductBarcodeImageIds: [],
  productImage: {},
  deletedProductImageIds: [],
});

export const buildProductDetailsRequest = (draft: ProductPanelDraft) => ({
  ...(draft.chosenImageFile
    ? { productImage: imageUploadPayload(draft.chosenImageFile) }
    : {}),
  productId: draft.productId,
  name: textOrNull(draft.name),
  description: textOrNull(draft.description),
  brand: textOrNull(draft.brand),
  category: textOrNull(draft.category),
  subCategory: textOrNull(draft.subCategory),
  netVolume: numberOrUndefined(draft.volume),
  netVolumeUnitOfMeasure: draft.volumeUnitOfMeasure,
  barcode: textOrNull(draft.barcode),
  dimensions: dimensionsPayload(draft.dimensions),
  weight: numberOrUndefined(draft.weight),
  weightUnitOfMeasure: draft.weightUnitOfMeasure,
  supplier: textOrNull(draft.supplier),
  temperatureControlType: textOrNull(draft.temperatureControlType),
});

export const buildSkuPanelUpdate = (
  selected: SelectedSKU,
  draft: SkuPanelDraft
) => {
  const base = baseSkuPayload(selected.sku);
  const editedSkuPayload = {
    ...base,
    skuId: draft.skuId.trim(),
    name: draft.name,
    description: textOrNull(draft.description),
    unitOfMeasure: draft.unitOfMeasure,
    amountOfProductInSku: numberOrUndefined(draft.amountOfProductInSku) ?? 1,
    barcode: textOrNull(draft.barcode),
    dimensions: dimensionsPayload(draft.dimensions),
    grossWeight: numberOrUndefined(draft.grossWeight),
    grossWeightUnitOfMeasure: draft.grossWeightUnitOfMeasure,
    grossVolume: numberOrUndefined(draft.grossVolume),
    grossVolumeUnitOfMeasure: draft.grossVolumeUnitOfMeasure,
    minimumOrderQuantity: numberOrUndefined(draft.minimumOrderQuantity),
    coPackingSupplier: textOrNull(draft.coPackingSupplier),
    metaData: withoutBlankAdditionalFields(draft.additionalFields).map(
      (field) => ({
        name: field.name.trim(),
        value: field.value,
        type: 'Text',
      })
    ),
  };
  return asUpdateRequest(
    editedSkuPayload,
    skuImageChangePayload(selected.sku, draft.chosenImageFile)
  );
};

export const buildPalletPanelUpdate = (
  selected: SelectedSKU,
  palletIndex: number,
  draft: PalletPanelDraft
) => {
  const base = baseSkuPayload(selected.sku);
  const existing = base.palletising;
  const palletId = existing[palletIndex]?.id ?? '';
  const palletising =
    palletIndex < existing.length
      ? existing.map((pallet, index) =>
          index === palletIndex ? palletDraftPayload(draft, palletId) : pallet
        )
      : [...existing, palletDraftPayload(draft, '')];
  return asUpdateRequest({ ...base, palletising });
};

export const buildPalletRemovalUpdate = (
  selected: SelectedSKU,
  palletIndex: number
) => {
  const base = baseSkuPayload(selected.sku);
  return asUpdateRequest({
    ...base,
    palletising: base.palletising.filter((_, index) => index !== palletIndex),
  });
};
